import { isWeb, useListPaymentMethodsQuery } from "@stepapp/core";
import { CategoryType } from "@stepapp/core/apiTypes";
import { Block, BottomSheet, spacing, Typography, useBreakpoints } from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { useAppSelector } from "../../hooks";
import PaymentOption from "./PaymentOption";
import { UpdatePayment } from "./UpdatePayment";

type Props = {
  paymentMethodId?: string;
  paymentId?: string;
  refetchReservation?: () => void;
  cycleDuration?: string;
  price?: number | null;
  startDate?: string | null;
  bundleName?: string;
  categoryType?: CategoryType | null;
};

export const Payment = ({
  paymentMethodId,
  paymentId,
  refetchReservation,
  cycleDuration,
  price,
  startDate,
  bundleName,
  categoryType = CategoryType.Cleaning,
}: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { reservationId } = useLocalSearchParams<{ reservationId: string }>();
  const [sheetVisible, setSheetVisible] = useState(false);
  const { md } = useBreakpoints();
  const isLogged = useAppSelector(({ user }) => user.id);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(paymentMethodId);

  const { data } = useListPaymentMethodsQuery({ skip: !isLogged });

  const card = useMemo(
    () => data?.listPaymentMethods?.find((pm) => pm.id === paymentMethodId),
    [data?.listPaymentMethods, paymentMethodId],
  );

  const toggleSheetVisibility = useCallback(
    (isVisible?: boolean) => {
      setSheetVisible((v) => (typeof isVisible === "boolean" ? isVisible : !v));
      setSelectedPaymentMethodId(paymentMethodId);
    },
    [paymentMethodId],
  );

  const handleOpenPaymentModal = useCallback(() => {
    if (isWeb) {
      return setSheetVisible(true);
    }

    navigate(`/calendar/${reservationId}/payment-method`);
  }, [reservationId, navigate]);

  const handleToggleBottomSheet = useCallback(() => {
    toggleSheetVisibility(false);
  }, [toggleSheetVisibility]);

  return (
    <Block paddingTop={2}>
      <Block flex={1} paddingBottom={2}>
        <Typography variant="sectionTitle">
          {t("screens.serviceDetails.changeServiceDetails", {
            context: categoryType?.toLowerCase(),
          })}
        </Typography>
      </Block>

      <PaymentOption
        icon="edit"
        label={card?.last4}
        onPress={handleOpenPaymentModal}
        type={card ? (card.brand as "visa") : "add"}
      />

      <BottomSheet
        avoidKeyboard
        contentStyle={md ? styles.bottomSheetWeb : styles.bottomSheetMobile}
        toggle={handleToggleBottomSheet}
        visible={sheetVisible}
      >
        <Block paddingVertical={1}>
          <Typography variant="sectionTitle">
            {t("screens.serviceSummary.paymentMethod")}
          </Typography>
        </Block>

        <UpdatePayment
          bundleName={bundleName}
          changePayment
          cycleDuration={cycleDuration}
          paymentId={paymentId}
          paymentMethodId={selectedPaymentMethodId}
          price={price}
          refetchReservation={refetchReservation}
          selectPaymentMethod={setSelectedPaymentMethodId}
          setSheetVisible={() => setSheetVisible(false)}
          startDate={startDate}
          reservationId={reservationId}
        />
      </BottomSheet>
    </Block>
  );
};

const styles = StyleSheet.create({
  bottomSheetMobile: {
    minWidth: spacing.unit * 50,
    padding: spacing.unit * 2.5,
    maxHeight: spacing.deviceHeight * 0.9,
  },
  bottomSheetWeb: {
    minWidth: spacing.unit * 80,
    padding: spacing.unit * 2.5,
    maxHeight: spacing.deviceHeight * 0.9,
  },
});
